import { useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { API } from "@/App";
import { cn } from "@/lib/utils";
import {
  Shield, AlertTriangle, AlertCircle, Tv, MessageSquare, Clock,
  ChevronRight, LogOut, ArrowLeft, Check, Eye, X, Send,
  Menu, ChevronLeft, FileText,
} from "lucide-react";

export default function ModeratorDashboard() {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [reports, setReports] = useState([]);
  const [submissions, setSubmissions] = useState([]);
  const [tab, setTab] = useState("reports");
  const [loading, setLoading] = useState(true);
  const [authInfo, setAuthInfo] = useState(null);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    // Check auth
    axios.get(`${API}/auth/me`, { withCredentials: true }).then(res => {
      if (!res.data.authenticated || res.data.level < 50) {
        navigate("/");
        return;
      }
      setAuthInfo(res.data);
    }).catch(() => navigate("/"));
  }, [navigate]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [statsRes, reportsRes, subsRes] = await Promise.all([
        axios.get(`${API}/moderator/dashboard`, { withCredentials: true }),
        axios.get(`${API}/moderator/reports`, { withCredentials: true }),
        axios.get(`${API}/moderator/submissions`, { withCredentials: true }).catch(() => ({ data: { submissions: [] } })),
      ]);
      setStats(statsRes.data);
      setReports(reportsRes.data.reports || []);
      setSubmissions(subsRes.data.submissions || []);
    } catch { }
    setLoading(false);
  }, []);

  useEffect(() => { if (authInfo) fetchData(); }, [authInfo, fetchData]);

  const handleReportAction = async (reportId, action, note = "") => {
    try {
      await axios.put(`${API}/moderator/reports/${reportId}`,
        { action, note }, { withCredentials: true });
      fetchData();
    } catch (e) { alert(e.response?.data?.detail || "Failed"); }
  };

  if (!authInfo) return null;

  const statCards = [
    { label: "Open Reports", value: stats?.open_reports || 0, icon: AlertCircle, color: "text-red-400", bg: "bg-red-500/10" },
    { label: "Urgent", value: stats?.urgent_reports || 0, icon: AlertTriangle, color: "text-amber-400", bg: "bg-amber-500/10" },
    { label: "Broken Streams", value: stats?.broken_streams || 0, icon: Tv, color: "text-orange-400", bg: "bg-orange-500/10" },
    { label: "Pending Reviews", value: stats?.pending_submissions || 0, icon: Clock, color: "text-blue-400", bg: "bg-blue-500/10" },
    { label: "Resolved Today", value: stats?.resolved_today || 0, icon: Check, color: "text-green-400", bg: "bg-green-500/10" },
    { label: "Escalated", value: stats?.escalated || 0, icon: Send, color: "text-purple-400", bg: "bg-purple-500/10" },
  ];

  const tabs = [
    { id: "reports", label: "Reports", count: (stats?.open_reports || 0) + (stats?.pending_reports || 0) },
    { id: "streams", label: "Broken Streams", count: stats?.broken_streams || 0 },
    { id: "submissions", label: "Channel Submissions", count: stats?.pending_submissions || 0 },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      {/* Header */}
      <header className="bg-slate-900 border-b border-slate-800 sticky top-0 z-30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 flex items-center justify-between h-14">
          <div className="flex items-center gap-3">
            <button onClick={() => setMobileMenuOpen(!mobileMenuOpen)} className="lg:hidden p-1.5 text-slate-400">
              <Menu size={20} />
            </button>
            <Shield className="text-green-400" size={20} />
            <h1 className="text-base font-bold text-white">Moderator Dashboard</h1>
          </div>
          <div className="flex items-center gap-3">
            <span className="text-xs text-slate-500 hidden sm:block">{authInfo.name || authInfo.id}</span>
            <Link to="/" className="text-xs text-slate-400 hover:text-white flex items-center gap-1">
              <ArrowLeft size={14} /> Back
            </Link>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6" data-testid="moderator-dashboard">
        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3 mb-6">
          {statCards.map((s, i) => (
            <div key={i} className="bg-slate-900 rounded-xl border border-slate-800 p-3.5" data-testid={`mod-stat-${s.label.toLowerCase().replace(/\s/g, '-')}`}>
              <div className="flex items-center gap-2 mb-2">
                <div className={cn("p-1.5 rounded-lg", s.bg)}><s.icon className={s.color} size={14} /></div>
              </div>
              <div className="text-2xl font-bold text-white">{s.value}</div>
              <div className="text-[11px] text-slate-500">{s.label}</div>
            </div>
          ))}
        </div>

        {/* Tabs */}
        <div className="flex gap-2 mb-4 overflow-x-auto pb-2">
          {tabs.map(t => (
            <button key={t.id} onClick={() => setTab(t.id)}
              className={cn("px-4 py-2 rounded-lg text-sm font-medium whitespace-nowrap transition-colors",
                tab === t.id ? "bg-green-600 text-white" : "bg-slate-900 text-slate-400 hover:text-white border border-slate-800")}
              data-testid={`mod-tab-${t.id}`}>
              {t.label}
              {t.count > 0 && <span className="ml-1.5 px-1.5 py-0.5 rounded-full text-[10px] bg-white/10">{t.count}</span>}
            </button>
          ))}
        </div>

        {/* Content */}
        <div className="bg-slate-900 rounded-xl border border-slate-800 overflow-hidden" data-testid="mod-content">
          {tab === "reports" && (
            <ReportQueue reports={reports.filter(r => r.type !== 'broken_stream')}
              onAction={handleReportAction} loading={loading} />
          )}
          {tab === "streams" && (
            <ReportQueue reports={reports.filter(r => r.type === 'broken_stream')}
              onAction={handleReportAction} loading={loading} />
          )}
          {tab === "submissions" && (
            <SubmissionQueue submissions={submissions} />
          )}
        </div>
      </div>
    </div>
  );
}

function ReportQueue({ reports, onAction, loading }) {
  if (loading) return <div className="p-8 text-center text-sm text-slate-500">Loading...</div>;
  if (reports.length === 0) {
    return (
      <div className="p-12 text-center">
        <Check className="mx-auto text-green-500/30 mb-3" size={40} />
        <p className="text-sm text-slate-500">No open reports. All caught up!</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-800/50">
      {reports.map(report => (
        <div key={report.id} className="p-4 hover:bg-slate-800/20 transition-colors" data-testid={`report-${report.id}`}>
          <div className="flex items-start justify-between gap-4">
            <div className="flex-grow">
              <div className="flex items-center gap-2 mb-1">
                <span className={cn("px-2 py-0.5 rounded text-[10px] font-semibold",
                  report.status === 'open' ? "bg-red-500/10 text-red-400" :
                  report.status === 'pending' ? "bg-amber-500/10 text-amber-400" :
                  report.status === 'escalated' ? "bg-purple-500/10 text-purple-400" :
                  "bg-green-500/10 text-green-400")}>
                  {report.status}
                </span>
                <span className="text-xs text-slate-500 capitalize">{report.type?.replace(/_/g, ' ') || 'general'}</span>
                {report.priority === 'urgent' && (
                  <AlertTriangle className="text-amber-400" size={12} />
                )}
              </div>
              <h4 className="text-sm font-medium text-white mb-1">{report.title || "Untitled Report"}</h4>
              <p className="text-xs text-slate-400 line-clamp-2">{report.description || "No description"}</p>
              <div className="text-[10px] text-slate-600 mt-1.5">
                {report.reporter_email && <span>From: {report.reporter_email} · </span>}
                {new Date(report.created_at).toLocaleString()}
              </div>
            </div>
            <div className="flex gap-1.5 flex-shrink-0">
              {report.status === 'open' && (
                <button onClick={() => onAction(report.id, 'assign')}
                  className="px-2.5 py-1.5 rounded bg-blue-600/10 text-blue-400 text-[11px] font-medium hover:bg-blue-600/20">
                  Assign to Me
                </button>
              )}
              {['open', 'pending'].includes(report.status) && (
                <>
                  <button onClick={() => onAction(report.id, 'resolve')}
                    className="px-2.5 py-1.5 rounded bg-green-600/10 text-green-400 text-[11px] font-medium hover:bg-green-600/20">
                    Resolve
                  </button>
                  <button onClick={() => onAction(report.id, 'escalate')}
                    className="px-2.5 py-1.5 rounded bg-amber-600/10 text-amber-400 text-[11px] font-medium hover:bg-amber-600/20">
                    Escalate
                  </button>
                  <button onClick={() => onAction(report.id, 'dismiss')}
                    className="px-2.5 py-1.5 rounded bg-slate-600/10 text-slate-400 text-[11px] font-medium hover:bg-slate-600/20">
                    Dismiss
                  </button>
                </>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

function SubmissionQueue({ submissions }) {
  if (submissions.length === 0) {
    return (
      <div className="p-12 text-center">
        <FileText className="mx-auto text-slate-700 mb-3" size={40} />
        <p className="text-sm text-slate-500">No pending channel submissions.</p>
      </div>
    );
  }
  return (
    <div className="divide-y divide-slate-800/50">
      {submissions.map(sub => (
        <div key={sub.id} className="p-4" data-testid={`submission-${sub.id}`}>
          <div className="flex items-center gap-2 mb-1">
            <span className={cn("px-2 py-0.5 rounded text-[10px] font-semibold",
              sub.status === 'pending' ? "bg-amber-500/10 text-amber-400" : "bg-green-500/10 text-green-400")}>
              {sub.status}
            </span>
          </div>
          <h4 className="text-sm font-medium text-white">{sub.channel_name || sub.name || "Channel Suggestion"}</h4>
          <p className="text-xs text-slate-400 mt-0.5">{sub.url || sub.stream_url || "—"}</p>
          <div className="text-[10px] text-slate-600 mt-1">{new Date(sub.created_at).toLocaleString()}</div>
        </div>
      ))}
    </div>
  );
}
